<script>
import Vue from 'vue'
import { GlobalBus } from '@services/bus'
import { MODAL, ACTION } from '@constants/Text'
import { ICONS } from '@modules/atendimento/constants/Text'
import apiAtendimento from '@modules/atendimento/api/apiAtendimento'
import SearchContactList from '@modules/atendimento/components/search-contact-list/search-contact-list.vue'

export default {
  name: 'ModalVincularContato',
  components: { SearchContactList },
  data() {
    return {
      icons: ICONS,
      busModal: new Vue(),
      loading: false,
      searching: false,
      searchQuery: '',
      searchResults: [],
      selectedCliente: null,
      idContato: null,
      idConexao: null,
      // Diferencia o label/CTA quando o vínculo é para um grupo de WhatsApp
      // (mesmo modal, mesma busca — só copy muda). Default false preserva
      // retrocompat com chamadores antigos que não passam `is_group`.
      isGroup: false,
      searchTimer: null,
    }
  },
  computed: {
    canConfirm() {
      return !!this.selectedCliente
    },
    confirmLabel() {
      return this.isGroup
        ? 'Vincular cliente ao grupo'
        : 'Vincular cliente ao contato'
    },
    modalTitle() {
      return this.isGroup ? 'Vincular cliente ao grupo' : 'Vincular a Cliente'
    },
  },
  created() {
    GlobalBus.$on(MODAL.ATENDIMENTO_VINCULAR_CONTATO, this.open)
  },
  beforeDestroy() {
    GlobalBus.$off(MODAL.ATENDIMENTO_VINCULAR_CONTATO, this.open)
    this.clearSearchTimer()
  },
  methods: {
    clear() {
      this.loading = false
      this.searching = false
      this.searchQuery = ''
      this.searchResults = []
      this.selectedCliente = null
      this.idContato = null
      this.idConexao = null
      this.isGroup = false
      this.clearSearchTimer()
    },

    open(params) {
      this.clear()
      if (params) {
        this.idContato = params.id_contato || null
        this.idConexao = params.id_conexao || null
        this.isGroup = !!params.isGroup
        if (params.cliente && params.cliente.id) {
          this.selectedCliente = { ...params.cliente }
        }
      }
      this.busModal.$emit('dialog', true)
    },

    clearSearchTimer() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
        this.searchTimer = null
      }
    },

    onSearchInput(value) {
      this.searchQuery = value
      this.clearSearchTimer()
      if (!value || value.length < 2) {
        this.searchResults = []
        return
      }
      this.searchTimer = setTimeout(this.search, 400)
    },

    async search() {
      if (this.searching) return
      this.searching = true
      try {
        const r = await apiAtendimento.buscarClientes({
          q: this.searchQuery,
          id_conexao: this.idConexao,
        })
        if (r && r.status === 200) {
          this.searchResults = r.value || []
        }
      } finally {
        this.searching = false
      }
    },

    selectCliente(cliente) {
      this.selectedCliente = cliente
    },

    clearSelectedCliente() {
      this.selectedCliente = null
      this.searchResults = []
    },

    formatCpf(cpf) {
      if (!cpf) return ''
      const digits = cpf.replaceAll(/\D/g, '')
      if (digits.length === 11) {
        return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
      }
      return cpf
    },

    getClienteSubtitle(cliente) {
      const parts = []
      if (cliente.cpf) parts.push(this.formatCpf(cliente.cpf))
      if (cliente.email) parts.push(cliente.email)
      return parts.join(' · ')
    },

    async confirm() {
      if (this.loading) return
      if (!this.canConfirm || !this.idContato) return

      this.loading = true
      try {
        const r = await apiAtendimento.vincularContato({
          id_contato: this.idContato,
          id_cliente_app: this.selectedCliente.id,
        })

        this.$showSnack({
          text: (r && r.message) || 'Erro inesperado',
          color: r && r.status === 200 ? 'success' : 'error',
          button: true,
        })
        if (r && r.status === 200) {
          this.busModal.$emit('dialog', false)
          GlobalBus.$emit(ACTION.ATENDIMENTO_CONTATO_VINCULADO, {
            id_contato: this.idContato,
            id_cliente_app: this.selectedCliente.id,
          })
        }
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<template>
  <BaseModal
    centerScreen
    :bus="busModal"
    persistent
    centerAndDecreasesPadding
    closeColor="#8e8e8e"
    backgroundColor="white"
  >
    <div class="pb-6">
      <div class="modal-title-container">
        <span class="modal-title" data-test="modal-title">{{ modalTitle }}</span>
      </div>

      <div class="modal-content">
        <div class="pa-4">
          <SearchContactList
            :items="searchResults"
            :selected="selectedCliente"
            :loading="searching"
            :display-field="['nome_cliente']"
            label="Buscar cliente"
            placeholder="Nome, CPF, e-mail ou telefone"
            noResultsText="Nenhum cliente encontrado"
            data-test="search-cliente"
            @search="onSearchInput"
            @select="selectCliente"
            @clear="clearSelectedCliente"
          >
            <template v-slot:item="{ item }">
              <v-avatar
                size="28"
                color="grey lighten-2"
                class="mr-2 flex-shrink-0"
              >
                <span class="az-text-small"
                  >{{ (item.nome_cliente || '?').charAt(0) }}
                </span>
              </v-avatar>
              <div class="flex-grow-1 overflow-hidden">
                <div
                  class="az-text-primary az-size-14 font-medium text-truncate"
                >
                  {{ item.nome_cliente }}
                </div>
                <div
                  v-if="getClienteSubtitle(item)"
                  class="az-text-small az-muted text-truncate"
                >
                  {{ getClienteSubtitle(item) }}
                </div>
              </div>
            </template>

            <template v-slot:selected="{ item, onClear }">
              <v-avatar size="28" color="primary" class="mr-2 flex-shrink-0">
                <span class="white--text az-size-12" style="font-size: 12px">
                  {{ (item.nome_cliente || '?').charAt(0) }}
                </span>
              </v-avatar>
              <div class="flex-grow-1 overflow-hidden">
                <div
                  class="az-text-primary az-size-14 font-medium text-truncate"
                >
                  {{ item.nome_cliente }}
                </div>
                <div v-if="item.cpf" class="az-text-small az-muted">
                  {{ formatCpf(item.cpf) }}
                </div>
              </div>
              <v-btn icon x-small class="ml-1" @click="onClear">
                <icon-svg :name="icons.close" size="14" color="#8E8E8E" />
              </v-btn>
            </template>
          </SearchContactList>
        </div>
      </div>
    </div>

    <template v-slot:actions="{ close }">
      <BaseButton underlined @click="close"> Cancelar </BaseButton>
      <BaseButton
        primary
        :loading="loading"
        :disabled="loading || !canConfirm"
        data-test="confirm-btn"
        @click="confirm"
      >
        {{ confirmLabel }}
      </BaseButton>
    </template>
  </BaseModal>
</template>
